import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';
import { Outlet, generatePath, useOutletContext } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { NewPageHeader } from '@sorare/core/src/components/navigation/NewPageHeader';
import { PageHeader as OldPageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  LeaguePageTabs,
  Tabs,
} from '@sorare/core/src/components/seo/League/Tabs';
import {
  FOOTBALL_LEAGUES_SLUG,
  FOOTBALL_LEAGUES_SLUG_CARDS,
  FOOTBALL_LEAGUES_SLUG_FIXTURES,
} from '@sorare/core/src/constants/__generated__/routes';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { withRouteQuery } from '@sorare/core/src/lib/navigation/withRouteQuery';

import {
  FootballSeoLeagueQuery,
  FootballSeoLeagueQueryVariables,
} from './__generated__/layout.graphql';
import { PageParams } from './__generated__/routeParams';

const FOOTBALL_SEO_LEAGUE_QUERY = gql`
  query FootballSeoLeagueQuery($slug: String!) {
    football {
      competition(slug: $slug) {
        slug
        displayName
        name
      }
    }
  }
` as TypedDocumentNode<FootballSeoLeagueQuery, FootballSeoLeagueQueryVariables>;

type LayoutData = FootballSeoLeagueQuery['football']['competition'];

export const useLayoutData = () => {
  return useOutletContext<LayoutData>();
};

export default withRouteQuery(
  ({ queryResult }) => {
    const {
      flags: { useNewNav = false },
    } = useFeatureFlags();
    const { slug } = useTypedParams<PageParams>();
    const competition = queryResult.data?.football.competition;
    const tabs: Record<LeaguePageTabs, string> = useMemo(() => {
      return {
        [LeaguePageTabs.OVERVIEW]: generatePath(FOOTBALL_LEAGUES_SLUG, {
          slug,
        }),
        [LeaguePageTabs.FIXTURES]: generatePath(
          FOOTBALL_LEAGUES_SLUG_FIXTURES,
          {
            slug,
          }
        ),
        [LeaguePageTabs.CARDS]: generatePath(FOOTBALL_LEAGUES_SLUG_CARDS, {
          slug,
        }),
      };
    }, [slug]);

    if (!competition) return null;

    const PageHeader = useNewNav ? NewPageHeader : OldPageHeader;
    return (
      <>
        <PageHeader text={competition.displayName} />
        <ContentWithStickyHeader header={<Tabs tabs={tabs} />}>
          <Outlet context={competition satisfies LayoutData} />
        </ContentWithStickyHeader>
      </>
    );
  },
  {
    query: FOOTBALL_SEO_LEAGUE_QUERY,
    queryOptions: ({ slug }: PageParams) => ({ variables: { slug } }),
  }
);
